/******************************************** STYLING *********************************************/
const s = require('./collapsible-section-heading.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import {IfFalsy, IfTruthy, isNumberLike} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {SolidMiniArrow, onSubmitKeyPress} from '@canimmunize/core-imports';
import {omit, isFalse} from 'mad-utils/lib/shared';

/******************************************* CONSTANTS ********************************************/
const defaultColour = `blue`;
const defaultFontClass = s[`collapsible-section-heading-font`]; // `helvetica-neue-medium`
const defaultPaddingBottom = 0;
const defaultPaddingLeft = 12;

const resolvePadding = (padVal: number | false) =>
    isFalse(padVal) ? null : isNumberLike(padVal) ? padVal : defaultPaddingBottom;

/*************************************** HELPER COMPONENTS ****************************************/
/**
 * Title or subtitle of a collapsible section
 * Returns null if no children
 *
 * Examples:
 *     <TitleOrSubtile wrapText={true}>
 *         Recommended Date May 17, 2018
 *     </TitleOrSubtitle>
 *
 *     <TitleOrSubtile wrapText={true}>
 *         <div>Shingles</div>
 *     </TitleOrSubtitle>
 *
 * @param {string|JSX.Element} children Title or subtitle to render
 * @param {boolean} wrapText If true, wrap title text if it's too long
 * @returns {Element|null} Styled title/subtitle el, or null if children is void
 *
 */
const TitleOrSubtitle = (props: {
    children: ReactChildren | null;
    wrapText: boolean;
}): JSX.Element | null => (
    <IfTruthy test={props.children}>
        <div className={cn(s[`header-margin`], `fw-normal`, !props.wrapText && `ws-nowrap`)}>
            {props.children}
        </div>
    </IfTruthy>
);

TitleOrSubtitle.displayName = `TitleOrSubtitle`;

/********************************************* EXPORT *********************************************/
/**
 * Heading text, beside an arrow indicating below section's visibility
 * Collapses section on click
 *
 * Example: Using the title text string itself as the child:
 *     <CollapsibleSectionHeading
 *         toggleCollapse={props.toggleMyRecordsCollapsed}
 *         isVisible={!props.isMyRecordsCollapsed}
 *     >
 *         Best Section Ever
 *     </CollapsibleSectionHeading>
 *
 * @param {string} children Should be title string or a component containing it
 *                          Wrap string in component to custom style title text
 *
 */
export const CollapsibleSectionHeading = ({
    toggleCollapse,
    children,
    isVisible,
    id,
    hideArrow,
    colour,
    paddingBottom,
    paddingLeft,
    fontClass,
    className,
    wrapText,
    innerHeadingProps,
    tabIndex,
    arrowStyle,
}: {
    toggleCollapse?: EventHandler;
    children: string;
    isVisible: boolean;
    id?: string;
    hideArrow?: boolean;
    colour?: string;
    paddingBottom?: number | false;
    paddingLeft?: number | false;
    fontClass?: string;
    className?: string;
    wrapText?: boolean; // default: false
    innerHeadingProps?: Partial<
        Omit<OmitReactProperties<HTMLAttributes.h>, 'id' | 'className' | 'style'>
    >;
    tabIndex?: number | null;
    arrowStyle?: React.CSSProperties;
}) => (
    <h2
        tabIndex={tabIndex === null ? null : tabIndex || 0}
        onKeyUp={toggleCollapse ? onSubmitKeyPress(toggleCollapse) : null}
        onClick={toggleCollapse ? toggleCollapse : null}
        className={cn(`flex`, fontClass || defaultFontClass, colour || defaultColour, className)}
        style={{
            paddingBottom: resolvePadding(paddingBottom),
            paddingLeft: resolvePadding(paddingLeft),
        }}
        aria-label={children}
        {...(id ? {id} : {})}
        {...omit(innerHeadingProps, [`className`, `style`, `id`])}
    >
        <div
            className={cn(s[`wrap-title-text`], `flex ai-center`, toggleCollapse ? `pointer` : ``)}
        >
            {/* Title text */}
            <TitleOrSubtitle wrapText={wrapText}>{children}</TitleOrSubtitle>
            {/* Arrow indicating if section is collapsed */}
            <IfFalsy test={hideArrow}>
                <div className={cn(s[`arrow-margin`], `flex align-self-center`, `mt-8`)}>
                    <SolidMiniArrow direction={isVisible ? `down` : `right`} style={arrowStyle} />
                </div>
            </IfFalsy>
        </div>
    </h2>
);

CollapsibleSectionHeading.displayName = `CollapsibleSectionHeading`;
